// src/components/autopilot/PersonSearchResults.js
import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Link,
  IconButton,
  Tooltip,
  Paper,
  SvgIcon
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import SchoolIcon from '@mui/icons-material/School';
import WorkIcon from '@mui/icons-material/Work';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import GitHubIcon from '@mui/icons-material/GitHub';
import TwitterIcon from '@mui/icons-material/Twitter';
import FacebookIcon from '@mui/icons-material/Facebook';

const ALLOWED_SOCIAL_NETWORKS = ['linkedin', 'facebook', 'twitter', 'github', 'stackoverflow'];

function StackOverflowIcon(props) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path d="M15 21h-10v-2h10v2zm6-11.665l-1.621-9.335-1.993.346 1.621 9.335 1.993-.346zm-5.964 6.937l-9.746-.975-.186 2.016 9.755.879.177-1.92zm.538-2.587l-9.276-2.608-.526 1.954 9.306 2.5.496-1.846zm1.204-2.413l-8.297-4.864-1.029 1.743 8.298 4.865 1.028-1.744zm1.866-1.467l-5.339-7.829-1.672 1.14 5.339 7.829 1.672-1.14zm-2.644 4.195v8h-12v-8h-2v10h16v-10h-2z" />
    </SvgIcon>
  );
}

function PersonSearchResults({ searchResults }) {
  const [profileSummaryDialog, setProfileSummaryDialog] = useState({
    open: false,
    title: 'Profile Summary',
    text: ''
  });

  const [workExperienceDialog, setWorkExperienceDialog] = useState({
    open: false,
    title: 'Work Experience',
    experiences: []
  });

  if (!searchResults || !searchResults.success || !searchResults.data || searchResults.data.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" sx={{ color: '#666' }}>
          No results found
        </Typography>
      </Box>
    );
  }

  const toTitleCase = (str) => {
    if (!str || typeof str !== 'string') return str || '';
    return str
      .trim()
      .split(/\s+/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const toSentenceCase = (str) => {
    if (!str || typeof str !== 'string') return str || '';
    const s = str.trim();
    return s.charAt(0).toUpperCase() + s.slice(1);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Present';
    // Handle YYYY-MM format
    if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}$/)) {
      const [year, month] = dateString.split('-');
      const date = new Date(parseInt(year), parseInt(month) - 1);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
    }
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString; // Return original if invalid
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
  };

  const truncateToWords = (text, maxWords = 100) => {
    if (!text) return '';
    const words = String(text).trim().split(/\s+/).filter(Boolean);
    if (words.length <= maxWords) return String(text).trim();
    return `${words.slice(0, maxWords).join(' ')}…`;
  };

  const getWordCount = (text) => {
    if (!text) return 0;
    return String(text).trim().split(/\s+/).filter(Boolean).length;
  };

  const openProfileSummaryDialog = (title, text) => {
    setProfileSummaryDialog({
      open: true,
      title: title || 'Profile Summary',
      text: text || ''
    });
  };

  const closeProfileSummaryDialog = () => {
    setProfileSummaryDialog((prev) => ({ ...prev, open: false }));
  };

  const openWorkExperienceDialog = (title, experiences) => {
    setWorkExperienceDialog({
      open: true,
      title: title || 'Work Experience',
      experiences: Array.isArray(experiences) ? experiences : []
    });
  };

  const closeWorkExperienceDialog = () => {
    setWorkExperienceDialog((prev) => ({ ...prev, open: false }));
  };

  const getInitials = (firstName, lastName) => {
    const first = firstName ? firstName.charAt(0).toUpperCase() : '';
    const last = lastName ? lastName.charAt(0).toUpperCase() : '';
    return `${first}${last}`;
  };

  const getSocialIcon = (network) => {
    if (!network) return null;
    const n = String(network).toLowerCase();
    const icons = {
      linkedin: LinkedInIcon,
      facebook: FacebookIcon,
      twitter: TwitterIcon,
      github: GitHubIcon,
      stackoverflow: StackOverflowIcon
    };
    return icons[n] || null;
  };

  const getSocialColor = (network) => {
    if (!network) return '#666';
    const n = String(network).toLowerCase();
    const colors = {
      linkedin: '#0077b5',
      facebook: '#1877f2',
      twitter: '#1da1f2',
      github: '#333',
      stackoverflow: '#f48024'
    };
    return colors[n] || '#666';
  };

  const getLocationFromPerson = (person) => {
    // If location is a string, use it
    if (typeof person.location === 'string' && person.location.trim()) {
      return person.location;
    }
    
    // Try to get location from most recent work experience (array format)
    if (person.workExperiences && Array.isArray(person.workExperiences) && person.workExperiences.length > 0) {
      const mostRecent = person.workExperiences[0];
      if (mostRecent.location) {
        // Handle location as array or string
        if (Array.isArray(mostRecent.location) && mostRecent.location.length > 0) {
          return mostRecent.location[0];
        } else if (typeof mostRecent.location === 'string' && mostRecent.location !== 'not specified') {
          return mostRecent.location;
        }
      }
    }
    
    return null;
  };

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', px: 1, py: 3, boxSizing: 'border-box' }}>
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: '#1BE885' }}>
        Who we found for you
        </Typography>
        <Typography variant="body1" sx={{ color: '#666', fontSize: '1.2rem' }}>
          Found {searchResults.data.length} {searchResults.data.length === 1 ? 'person' : 'people'}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(390px, 1fr))',
          gap: 2,
          alignItems: 'stretch',
          width: '100%',
        }}
      >
        {searchResults.data.map((person, index) => (
          <Box key={index} sx={{ display: 'flex', minWidth: 0 }}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 4,
              border: '1px solid',
              borderColor: 'divider',
              borderLeft: '4px solid #1BE885',
              bgcolor: 'background.paper',
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
              overflow: 'hidden',
              transition: 'all 0.2s ease',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              '&:hover': {
                boxShadow: '0 4px 14px rgba(0,0,0,0.1)',
                borderColor: 'rgba(27, 232, 133, 0.4)'
              }
            }}
          >
            <CardContent sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, '&:last-child': { pb: 3 } }}>
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              {/* Header Section */}
                <Box sx={{ display: 'flex', gap: 3 }}>

                  {/* Basic Info */}
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                      <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        {toTitleCase(person.firstName || '')} {toTitleCase(person.lastName || '')}
                      </Typography>
                      {person.linkedinLink && (
                        <Tooltip title="View LinkedIn Profile">
                          <IconButton
                            component={Link}
                            href={`https://${person.linkedinLink}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            size="small"
                            sx={{ color: '#0077b5' }}
                          >
                            <LinkedInIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                    <Divider />
                    {person.currentPosition && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <WorkIcon sx={{ fontSize: 18, color: '#666' }} />
                        <Typography variant="h6" sx={{ color: '#333', fontWeight: 500 }}>
                          {toTitleCase(person.currentPosition)}
                        </Typography>
                      </Box>
                    )}

                    {person.currentOrg && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <BusinessIcon sx={{ fontSize: 18, color: '#666' }} />
                        <Typography variant="body1" sx={{ color: '#666' }}>
                          {toTitleCase(person.currentOrg)}
                        </Typography>
                      </Box>
                    )}

                    {(() => {
                      const location = getLocationFromPerson(person);
                      return location ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <LocationOnIcon sx={{ fontSize: 18, color: '#666' }} />
                          <Typography variant="body2" sx={{ color: '#666' }}>
                            {toTitleCase(location)}
                          </Typography>
                        </Box>
                      ) : null;
                    })()}

                    {/* Title */}
                    {person.title && (
                      <Box sx={{ mb: 1, mt: 1 }}>
                        <Typography variant="body1" sx={{ color: '#666', fontStyle: 'italic' }}>
                          {toTitleCase(person.title)}
                        </Typography>
                      </Box>
                    )}

                    {/* Highest Education */}
                    {person.highestEducation && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <SchoolIcon sx={{ fontSize: 18, color: '#666' }} />
                        <Typography variant="body2" sx={{ color: '#666' }}>
                          {toTitleCase(person.highestEducation)}
                        </Typography>
                      </Box>
                    )}

                    {/* Total Experience */}
                    {person.totalExperience && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <WorkIcon sx={{ fontSize: 18, color: '#666' }} />
                        <Typography variant="body2" sx={{ color: '#666' }}>
                          {person.totalExperience} {person.totalExperience === 1 ? 'year' : 'years'} of experience
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>

                <Divider />

                {/* Social Profiles - only linkedin, facebook, twitter, github, stackoverflow */}
                {(() => {
                  const allowed = person.socialProfiles && Array.isArray(person.socialProfiles)
                    ? person.socialProfiles.filter((p) =>
                        ALLOWED_SOCIAL_NETWORKS.includes(String(p?.network || '').toLowerCase())
                      )
                    : [];
                  if (allowed.length === 0) return null;
                  return (
                    <Box>
                      
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#333', alignSelf: 'center' }}>
                        Social Profiles
                      </Typography>
                        {allowed.map((profile, idx) => {
                          const Icon = getSocialIcon(profile.network);
                          if (!Icon) return null;
                          const label = profile.network ? toTitleCase(profile.network) : 'Social';
                          const href = profile.url ? `https://${profile.url}` : undefined;
                          const color = getSocialColor(profile.network);
                          return (
                            <Tooltip key={idx} title={label}>
                              <span>
                                <IconButton
                                  component={href ? Link : 'span'}
                                  href={href}
                                  target={href ? '_blank' : undefined}
                                  rel={href ? 'noopener noreferrer' : undefined}
                                  size="small"
                                  disabled={!href}
                                  sx={{
                                    color,
                                    '&:hover': {
                                      bgcolor: `${color}14`,
                                      color
                                    }
                                  }}
                                >
                                  <Icon sx={{ fontSize: 22 }} />
                                </IconButton>
                              </span>
                            </Tooltip>
                          );
                        })}
                      </Box>
                    </Box>
                  );
                })()}

              </Box>
              <Divider />
                {/* Work Experience */}
                {person.workExperiences && Array.isArray(person.workExperiences) && person.workExperiences.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: '#333' }}>
                      Work Experience
                    </Typography>
                    {person.workExperiences.slice(0, 3).map((exp, expIdx) => {
                      const locationStr = Array.isArray(exp.location)
                        ? exp.location.join(', ')
                        : exp.location || '';
                      const tenure = exp.startDate && exp.endDate
                        ? `${formatDate(exp.startDate)} - ${formatDate(exp.endDate)}`
                        : exp.startDate
                        ? `${formatDate(exp.startDate)} - Present`
                        : '';
                      return (
                        <Paper
                          key={expIdx}
                          elevation={0}
                          sx={{
                            p: 1,
                            mb: 0.5,
                            bgcolor: '#f8f9fa',
                            borderRadius: 1,
                            borderLeft: '3px solid #1BE885'
                          }}
                        >
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1BE885', mb: 0.5 }}>
                            {exp.company ? toTitleCase(exp.company) : 'Unknown Company'}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {toTitleCase(exp.title)}
                          </Typography>
                          {tenure && (
                            <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>
                              {tenure}
                            </Typography>
                          )}
                          {locationStr && (
                            <Typography variant="body2" sx={{ color: '#666' }}>
                              {toTitleCase(locationStr)}
                            </Typography>
                          )}
                        </Paper>
                      );
                    })}

                    {person.workExperiences.length > 3 && (
                      <Button
                        size="small"
                        variant="text"
                        sx={{ mt: 0.5, px: 0, textTransform: 'none' }}
                        onClick={() =>
                          openWorkExperienceDialog(
                            toTitleCase(`${person.firstName || ''} ${person.lastName || ''}`.trim()) || 'Work Experience',
                            person.workExperiences.slice(3)
                          )
                        }
                      >
                        Show more
                      </Button>
                    )}
                  </Box>
                )}

                {/* Card footer - chat-style metadata */}
                <Box sx={{  borderTop: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Result {index + 1} of {searchResults.data.length}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Box>
        ))}
      </Box>

      <Dialog open={profileSummaryDialog.open} onClose={closeProfileSummaryDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{profileSummaryDialog.title}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ color: 'text.primary', whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
            {toSentenceCase(profileSummaryDialog.text)}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeProfileSummaryDialog} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={workExperienceDialog.open} onClose={closeWorkExperienceDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{workExperienceDialog.title}</DialogTitle>
        <DialogContent dividers>
          {workExperienceDialog.experiences.map((exp, expIdx) => {
            const locationStr = Array.isArray(exp.location) ? exp.location.join(', ') : exp.location || '';
            const tenure = exp.startDate && exp.endDate
              ? `${formatDate(exp.startDate)} - ${formatDate(exp.endDate)}`
              : exp.startDate
              ? `${formatDate(exp.startDate)} - Present`
              : '';
            return (
              <Paper
                key={expIdx}
                elevation={0}
                sx={{
                  p: 2,
                  mb: expIdx === workExperienceDialog.experiences.length - 1 ? 0 : 1.5,
                  bgcolor: '#f8f9fa',
                  borderRadius: 1,
                  borderLeft: '3px solid #1BE885'
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1BE885', mb: 0.5 }}>
                  {exp.company ? toTitleCase(exp.company) : 'Unknown Company'}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {toTitleCase(exp.title)}
                </Typography>
                {tenure && (
                  <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>
                    {tenure}
                  </Typography>
                )}
                {locationStr && (
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    {toTitleCase(locationStr)}
                  </Typography>
                )}
              </Paper>
            );
          })}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeWorkExperienceDialog} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default PersonSearchResults;
